import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Select,
  Avatar,
  Descriptions,
  Button,
  Modal,
  Input,
  message,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTable } from '@/hooks/useTable';
import {
  getUsesCoinsList,
  updateUserCoins,
  getUserCoinsInfo,
} from '@/api/userCoins';

const coinsTypeOptions = [
  { label: '全部', value: '' },
  { label: '增加硬币', value: 1 },
  { label: '扣减硬币', value: 2 },
];

enum CoinsTypeEnum {
  Coins = 1,
  Givs
}

const CoinsTypeMap = new Map([
  [CoinsTypeEnum.Coins, "充值金币"],
  [CoinsTypeEnum.Givs, "活动金币"],
])

const UserCoinsPage: React.FC = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [coinsForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [adjustType, setAdjustType] = useState<1 | 0>(1);

  // 获取用户硬币信息
  const { data: userCoinsInfo, refetch: refetchUserInfo } = useQuery({
    queryKey: ['user-coins-info', userId],
    queryFn: async () => {
      const response = await getUserCoinsInfo(userId);
      return response.data;
    },
  });

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
    refresh,
  } = useTable({
    queryKey: ['user-coins-list', userId].join(','),
    queryFn: async (current, size, params) => {
      const response = await getUsesCoinsList({
        ...params,
        userId,
        ...form.getFieldsValue(),
        currentPage: current,
        pageSize: size,
      });
      return response.data.data;
    },
  });

  const handleSearch = async () => {
    refresh();
  };

  const handleReset = () => {
    form.resetFields();
    refresh();
  };

  const showCoinsModal = (type: 1 | 0) => {
    setAdjustType(type);
    setIsModalVisible(true);
    coinsForm.resetFields();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    coinsForm.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await coinsForm.validateFields();
      await updateUserCoins({
        ...values,
        userId,
        operatorType: adjustType,
        coins: Number(values.coins),
      });
      message.success('硬币调整成功');
      setIsModalVisible(false);
      coinsForm.resetFields();

      form.resetFields();
      refresh();
      refetchUserInfo();
    } catch (error) {
      console.error('硬币调整失败:', error);
      message.error('硬币调整失败');
    }
  };

  const columns = [
    // {
    //   title: '硬币ID',
    //   dataIndex: 'id',
    //   width: 200,
    // },
    {
      title: '硬币类型',
      dataIndex: 'coinsOpType',
      width: 100,
      render: (type: number) => (type === 1 ? '获得' : '消耗'),
    },
    {
      title: '硬币值',
      dataIndex: 'coins',
      width: 100,
      render: (coins: number, record: any) => (
        <span
          style={{ color: record.coinsOpType === 1 ? '#52c41a' : '#ff4d4f' }}
        >
          {record.coinsOpType === 1 ? '+' : '-'}
          {coins}
        </span>
      ),
    },
    {
      title: '硬币场景',
      dataIndex: 'coinsSenseTitle',
      width: 150,
    },
    {
      title: '场景描述',
      dataIndex: 'coinsSenseDesc',
      width: 200,
    },
    {
      title: '场景图标',
      dataIndex: 'coinsSenseIconUrl',
      width: 100,
      render: (url: string) => (url ? <Avatar src={url} /> : '-'),
    },
    {
      title: '业务ID',
      dataIndex: 'bizId',
      width: 200,
      render: (bizId: string) => bizId || '-',
    },
    {
      title: '额外数据',
      dataIndex: 'extData',
      width: 200,
      render: (extData: any) => (extData ? JSON.stringify(extData) : '-'),
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 100,
      render: (source: string) => source || '-',
    },
    {
      title: '金币类型',
      dataIndex: 'coinSourceType',
      width: 100,
      render: (type: number) => {
        const map: Record<number, { text: string; color: string }> = {
          1: { text: '活动', color: '#52c41a' },
          2: { text: '会员', color: '#faad14' },
          3: { text: '充值', color: '#1890ff' },
          4: { text: '收益', color: '#722ed1' },
        };
        const item = map[type];
        return item ? <span style={{ color: item.color, fontWeight: 600 }}>{item.text}</span> : '-';
      },
    },
    {
      title: '操作人',
      dataIndex: 'createUser',
    },
    {
      title: '操作时间',
      dataIndex: 'createTime',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <Card
      title="用户硬币明细"
      extra={<Button onClick={() => navigate(-1)}>返回</Button>}
    >
      {userCoinsInfo && (
        <Card style={{ marginBottom: 16 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}
          >
            <Avatar size={64} src={userCoinsInfo.headImageUrl} />
            <div style={{ marginLeft: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                {userCoinsInfo.name}
              </div>
              <div>用户ID: {userCoinsInfo.userNumber}</div>
            </div>
          </div>
          <Descriptions column={3}>
            <Descriptions.Item label="可用充值金币">
              {userCoinsInfo.remainingCoins}
            </Descriptions.Item>
            <Descriptions.Item label="累计获得充值金币">
              {userCoinsInfo.totalCoins}
            </Descriptions.Item>
            <Descriptions.Item label="累计消耗充值金币">
              {userCoinsInfo.usageCoins}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={3}>
            <Descriptions.Item label="可用活动金币">
              {userCoinsInfo.remainingGivs}
            </Descriptions.Item>
            <Descriptions.Item label="累计获得活动金币">
              {userCoinsInfo.totalGivs}
            </Descriptions.Item>
            <Descriptions.Item label="累计消耗活动金币">
              {userCoinsInfo.usageGivs}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={3}>
            <Descriptions.Item label="会员金币">
              <span style={{ color: '#faad14', fontWeight: 600 }}>
                {userCoinsInfo.memberCoins ?? 0}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="收益金币">
              <span style={{ color: '#722ed1', fontWeight: 600 }}>
                {userCoinsInfo.earningGoldCoin ?? 0}
              </span>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={() => showCoinsModal(1)}
              style={{ marginRight: 16 }}
            >
              增加硬币
            </Button>
            <Button danger onClick={() => showCoinsModal(0)}>
              扣减硬币
            </Button>
          </div>
        </Card>
      )}
      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item
          name="coinsOpType"
          label="硬币类型"
          initialValue={coinsTypeOptions[0].value}
        >
          <Select style={{ width: 120 }} options={coinsTypeOptions} />
        </Form.Item>
        <Form.Item>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </div>
        </Form.Item>
      </Form>
      <Modal
        title={adjustType === 1 ? '增加硬币' : '扣减硬币'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Form form={coinsForm} layout="vertical">
          <Form.Item
            name="coins"
            label="硬币数量"
            rules={[
              { required: true, message: '请输入硬币数量' },
              { pattern: /^[1-9]\d*$/, message: '请输入大于0的整数' },
            ]}
          >
            <Input placeholder="请输入硬币数量" />
          </Form.Item>
          <Form.Item
            name="type"
            label="硬币类型"
            rules={[
              { required: true, message: '请选择硬币类型' },
            ]}
          >
            <Select>
              {
                [...CoinsTypeMap.keys()].map(key => {
                  const label = CoinsTypeMap.get(key);
                  return <Select.Option key={key} value={key}>{label}</Select.Option>
                })
              }
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(_, index) => index ?? 0}
        scroll={{ x: 1200 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
        }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default UserCoinsPage;
